import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Stack,
  Fade,
  Avatar,
  Rating,
  Chip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ChatIcon from '@mui/icons-material/Chat';
import MoodIcon from '@mui/icons-material/Mood';
import CreateIcon from '@mui/icons-material/Create';
import SchoolIcon from '@mui/icons-material/School';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { apiClient, ENDPOINTS } from '../services/apiConfig';
import Seo from '../components/Seo';

const Home: React.FC = () => {
  // eslint-disable-next-line
  const [latestCrafts, setLatestCrafts] = useState<any[]>([]);
  const [visitCount, setVisitCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchLatestCrafts = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
        const response = await apiClient.get(ENDPOINTS.CREATIVE_CRAFTS, config);
        const sorted = [...response.data].sort(
          (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setLatestCrafts(sorted.slice(0, 3));
      } catch (err) {
        console.error('Failed to load latest crafts:', err);
      }
    };
    fetchLatestCrafts();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchVisits = async () => {
      try {
        const { data } = await apiClient.post<{ count: number }>(ENDPOINTS.METRICS_HOME_VISIT);
        if (!isCancelled) {
          setVisitCount(typeof data?.count === 'number' ? data.count : null);
        }
      } catch {
        if (!isCancelled) {
          setVisitCount(null);
        }
      }
    };

    fetchVisits();

    return () => {
      isCancelled = true;
    };
  }, []); // run once on mount

  return (
    <>
      <Seo
        title="AI Mental Health Companion"
        description="MoodCraft is an AI-powered mental health companion for mood tracking, journaling, self-help resources, and emotional wellbeing."
        canonicalPath="/"
      />
      <Box
        sx={{
          background: 'linear-gradient(135deg, #5c6bc0 0%, #26a69a 100%)',
          color: 'white',
          pt: 10,
          pb: 10,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
        }} />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    component="h1"
                    variant="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      fontFamily: '"Lato", sans-serif'
                    }}
                  >
                    Craft Your Path to Emotional Wellbeing
                  </Typography>
                  <Typography 
                    variant="h5" 
                    paragraph
                    sx={{
                      fontWeight: 400,
                      mb: 3,
                      opacity: 0.9,
                      fontFamily: '"Lato", sans-serif'
                    }}
                  >
                    Your personal AI-powered mental health companion for daily emotional support and growth
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{
                      mb: 4,
                      maxWidth: '90%',
                      opacity: 0.8,
                      fontFamily: '"Quicksand", sans-serif'
                    }}
                  >
                    MoodCraft combines personalized emotional support, creative expression tools, and evidence-based therapeutic techniques to help you understand and improve your mental wellbeing.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button 
                      variant="contained" 
                      size="large"
                      component={Link} 
                      to="/register"
                      sx={{
                        bgcolor: 'secondary.main',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontFamily: '"Quicksand", sans-serif',
                        '&:hover': {
                          bgcolor: 'secondary.dark',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      Get Started Free
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="large"
                      component={Link} 
                      to="/login"
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontFamily: '"Quicksand", sans-serif',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Fade in={true} timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Paper
                    elevation={8}
                    sx={{
                      width: '80%',
                      height: '80%',
                      borderRadius: 4,
                      overflow: 'hidden',
                      transform: 'rotate(-5deg)',
                      position: 'absolute',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/logo.png"
                      alt="MoodCraft App"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Paper>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 6,
              fontFamily: '"Lato", sans-serif'
            }}
          >
            How MoodCraft Works
          </Typography>
          
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 0 } }}>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    color: 'white',
                    mb: 2
                    
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>1</Typography>
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  Track Your Mood
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  Log your daily emotions and track patterns over time to gain insights into your emotional wellbeing.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 0 } }}>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    color: 'white',
                    mb: 2
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>2</Typography>
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  Get AI Support
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  Receive personalized coping strategies and emotional support from our AI companion.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    color: 'white',
                    mb: 2
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>3</Typography>
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  Practice & Improve
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  Use our self-help modules and creative outlets to develop healthy coping mechanisms.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                position: 'relative',
                display: 'inline-block',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -10,
                  left: '50%',
                  width: 80,
                  height: 4,
                  bgcolor: 'secondary.main',
                  transform: 'translateX(-50%)',
                  borderRadius: 2
                },
                fontFamily: '"Quicksand", sans-serif'
              }}
            >
              Key Features
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                mt: 2
              }}
            >
              Discover the tools and features designed to support your mental wellbeing journey
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  '& .MuiCardMedia-root': {
                    transform: 'scale(1.05)'
                  }
                },
              }}>
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.5s ease',
                  }}
                >
                  <ChatIcon sx={{ fontSize: 80, color: 'white' }} />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                    AI Companion
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ fontFamily: '"Lato", sans-serif' }}>
                    A friendly, non-judgmental virtual companion that offers personalized emotional support and tailored coping strategies based on your unique needs.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label="24/7 Support" size="small" color="primary" variant="outlined" />
                    <Chip label="Personalized" size="small" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  '& .MuiCardMedia-root': {
                    transform: 'scale(1.05)'
                  }
                },
              }}>
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    bgcolor: 'secondary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.5s ease',
                  }}
                >
                  <MoodIcon sx={{ fontSize: 80, color: 'white' }} />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                    Mood Tracking
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Log your daily emotions and visualize patterns over time with interactive charts and insights to better understand your emotional triggers and trends.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label="Analytics" size="small" color="secondary" variant="outlined" />
                    <Chip label="Insights" size="small" color="secondary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  '& .MuiCardMedia-root': {
                    transform: 'scale(1.05)'
                  }
                },
              }}>
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    bgcolor: 'primary.dark',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.5s ease',
                  }}
                >
                  <CreateIcon sx={{ fontSize: 80, color: 'white' }} />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                    Creative Outlets
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ fontFamily: '"Lato", sans-serif' }}>
                    Express your emotions through guided journaling, poetry writing, digital drawing, and music creation tools designed to help process feelings in healthy ways.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label="Journaling" size="small" color="primary" variant="outlined" />
                    <Chip label="Art Therapy" size="small" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  '& .MuiCardMedia-root': {
                    transform: 'scale(1.05)'
                  }
                },
              }}>
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    bgcolor: 'secondary.dark',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.5s ease',
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 80, color: 'white' }} />
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                    Self-Help Modules
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ fontFamily: '"Lato", sans-serif' }}>
                    Interactive learning modules based on evidence-based therapeutic approaches including CBT, DBT, mindfulness, and positive psychology practices.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label="Evidence-based" size="small" color="secondary" variant="outlined" />
                    <Chip label="Interactive" size="small" color="secondary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/register"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontFamily: '"Quicksand", sans-serif',
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Explore All Features
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: 1, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 4,
              fontFamily: '"Quicksand", sans-serif',
            }}
          >
            What Our Users Say
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              }}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <FormatQuoteIcon sx={{ fontSize: 40, color: 'primary.light', mr: 1 }} />
                </Box>
                <Typography variant="body1" paragraph sx={{ flex: 1, fontStyle: 'italic' }}>
                  MoodCraft has been a game-changer for my anxiety. The AI companion feels like talking to a friend who always knows what to say. The mood tracking helped me identify triggers I never noticed before.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>S</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sarah K.</Typography>
                    <Rating value={5} readOnly size="small" />
                  </Box>
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              }}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <FormatQuoteIcon sx={{ fontSize: 40, color: 'primary.light', mr: 1 }} />
                </Box>
                <Typography variant="body1" paragraph sx={{ flex: 1, fontStyle: 'italic' }}>
                  The creative outlet features have given me a healthy way to process my emotions. I've started journaling daily and it's made a huge difference in how I handle stress. Highly recommend!
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>M</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Michael T.</Typography>
                    <Rating value={4.5} precision={0.5} readOnly size="small" />
                  </Box>
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              }}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <FormatQuoteIcon sx={{ fontSize: 40, color: 'primary.light', mr: 1 }} />
                </Box>
                <Typography variant="body1" paragraph sx={{ flex: 1, fontStyle: 'italic' }}>
                  The self-help modules are incredibly well-designed. I've learned so many practical techniques for managing my depression. It feels like having a therapist in my pocket 24/7.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.dark', mr: 2 }}>J</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Jamie L.</Typography>
                    <Rating value={5} readOnly size="small" />
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 6,
              fontFamily: '"Quicksand", sans-serif',
            }}
          >
            Frequently Asked Questions
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  Is my data private and secure?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  Yes, we take privacy seriously. All your data is encrypted and we never share your personal information with third parties. You can delete your data at any time.
                </Typography>
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  Can MoodCraft replace therapy?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  MoodCraft is designed as a supplement to professional care, not a replacement. While our tools can help with daily emotional management, we recommend working with a licensed therapist for clinical concerns.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  How much does MoodCraft cost?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  MoodCraft offers a free basic plan with limited features. Premium plans start at $9.99/month and include unlimited access to all features, including advanced AI support and all self-help modules.
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontFamily: '"Quicksand", sans-serif' }}>
                  How does the AI companion work?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
                  Our AI companion uses natural language processing to understand your emotions and provide personalized support. It's trained on evidence-based therapeutic approaches but adapts to your unique needs over time.
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              component={Link} 
              to="/register"
              sx={{
                bgcolor: 'primary.main',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontFamily: '"Quicksand", sans-serif',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Visitor counter at the bottom */}
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Lato", sans-serif' }}>
          {visitCount !== null
            ? `${visitCount} visitors have been here`
            : 'Loading visits...'}
        </Typography>
      </Box>
    </>
  );
};

export default Home;
